import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { acceptedFiles } from "@/constants";
import { UserState } from "@/context/User/UserReducer";
import { addPlatform } from "@/services/Platform.service";
import { AddPlatformParams } from "@/types/platform.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { MdOutlineCloudUpload } from "react-icons/md";
import { toast } from "sonner";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";
import AttachmentPreview, { FilePreview } from "./AttachmentPreview";

const formSchema = z.object({
	platformname: z.string(),
	attachment: z.instanceof(File).optional(),
});

const CreatePlatform: React.FC<{ user: UserState | null }> = ({ user }) => {
	const [open, setOpen] = useState(false);
	const [uploadedFile, setUploadedFile] = useState<FilePreview | null>(null);

	const queryClient = useQueryClient();

	const createPlatform = useMutation({
		mutationKey: ["platform_create"],
		mutationFn: (data: AddPlatformParams) =>
			addPlatform(user?.accessToken!, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["platforms"],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			form.reset();
			toast.success("Success", {
				description: `Platform ${variables.platformname} has been successfully created`,
			});
			createPlatform.reset();
		},
		onError: (err) => {
			toast.error("Error!", {
				description: "Error adding platform, try again",
			});
			console.log(err);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
	});

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const rawFiles = Array.from(event.target.files || []);
		const files = rawFiles.filter((file) => {
			if (acceptedFiles.includes(file.type)) return true;
			toast.error("File type not supported");
			return false;
		});
		const logo = files[0];
		form.setValue("attachment", logo);
		const preview = {
			filename: logo.name,
			preview: URL.createObjectURL(logo),
		};
		setUploadedFile(preview);
	};

	const removeFile = (index: number) => {
		form.setValue("attachment", undefined);
		setUploadedFile(null);
	};

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		if (!data.attachment) {
			toast.error("Please upload a logo");
			return;
		}
		createPlatform.mutate({
			platformname: data.platformname,
			attachment: data.attachment,
		});
	};

	const disabled = () => {
		if (createPlatform.isPending) return true;
		if (createPlatform.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Add Platform</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Add Platform
					</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="platformname"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Name of Platform</FormLabel>
									<FormControl>
										<Input type="text" {...field} disabled={disabled()} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="attachment"
							render={({ field }) => (
								<FormItem className="mb-2">
									<FormLabel>Attachments</FormLabel>
									<FormLabel className="flex items-center justify-center border-dashed border-2 border-gray-300 rounded-lg p-4 cursor-pointer hover:bg-muted">
										<div className="flex flex-col items-center justify-center gap-2">
											<MdOutlineCloudUpload className="text-4xl" />
											<p>
												<b>Click to upload</b>
											</p>
											<p className="font-light">SVG, PNG, JPG or PDF</p>
										</div>
										<FormControl className="hidden">
											<Input
												type="file"
												onChange={(e) => {
													handleFileChange(e);
												}}
												disabled={disabled()}
											/>
										</FormControl>
									</FormLabel>
									<FormMessage />
								</FormItem>
							)}
						/>
						{uploadedFile ? (
							<AttachmentPreview
								files={[uploadedFile]}
								deleteFile={removeFile}
								disabled={disabled()}
							/>
						) : null}
						<Button
							type="submit"
							className="mt-4 w-full"
							disabled={createPlatform.isPending || createPlatform.isSuccess}
						>
							{createPlatform.isPending ? "Creating..." : "Create Platform"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default CreatePlatform;
